import { useRef, useState, useCallback } from 'react';
import { useGeneratorStore } from '../generatorStore';

function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = 512;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });
}

export default function StyleReferenceStep() {
  const { params, setParams, setStep } = useGeneratorStore();
  const [preview, setPreview] = useState<string | undefined>(params.styleReference);
  const [fileInfo, setFileInfo] = useState<{ name: string; size: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('请上传图片文件');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('图片大小不能超过 2MB');
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      setPreview(dataUrl);
      setParams({ styleReference: dataUrl });
      setFileInfo({
        name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
      });
    } catch {
      setError('图片处理失败，请重试');
    }
  }, [setParams]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }, [handleFile]);

  const removeFile = useCallback(() => {
    setPreview(undefined);
    setFileInfo(null);
    setError(null);
    setParams({ styleReference: undefined });
  }, [setParams]);

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>风格参考（可选）</h2>
      <p style={subtitleStyle}>上传一张参考图，AI 会尽量匹配其艺术风格</p>

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          ...dropZoneStyle,
          borderColor: isDragging ? 'rgba(124,77,255,0.6)' : 'rgba(255,255,255,0.15)',
          background: isDragging ? 'rgba(124,77,255,0.08)' : 'rgba(255,255,255,0.03)',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onFileChange}
          style={{ display: 'none' }}
        />
        {preview ? (
          <div style={previewWrapStyle}>
            <img src={preview} alt="style reference" style={previewImgStyle} />
            {fileInfo && (
              <div style={fileInfoStyle}>
                <div style={fileNameStyle}>{fileInfo.name}</div>
                <div style={fileSizeStyle}>{fileInfo.size}</div>
              </div>
            )}
            <button onClick={removeFile} style={removeBtnStyle}>
              删除
            </button>
          </div>
        ) : (
          <div style={dropHintStyle}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>{'\u{1F4F7}'}</div>
            <div style={{ fontSize: '15px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px' }}>
              拖拽图片到此处，或
              <span
                onClick={() => fileRef.current?.click()}
                style={{ color: '#7c4dff', cursor: 'pointer', marginLeft: '4px' }}
              >
                点击选择
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
              支持 JPG、PNG，最大 2MB
            </div>
          </div>
        )}
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <div style={actionsStyle}>
        <button onClick={() => setStep('config')} style={btnSecondaryStyle}>
          ← 上一步
        </button>
        <button onClick={() => setStep('generating')} style={btnPrimaryStyle}>
          开始生成 →
        </button>
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
  padding: '40px 20px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '28px',
  fontWeight: 'bold',
  color: '#fff',
  marginBottom: '8px',
  textAlign: 'center',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(255,255,255,0.5)',
  marginBottom: '40px',
  textAlign: 'center',
};

const dropZoneStyle: React.CSSProperties = {
  border: '2px dashed rgba(255,255,255,0.15)',
  borderRadius: '16px',
  padding: '40px 20px',
  textAlign: 'center',
  transition: 'all 0.2s',
  marginBottom: '16px',
};

const dropHintStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const previewWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
};

const previewImgStyle: React.CSSProperties = {
  maxWidth: '200px',
  maxHeight: '200px',
  borderRadius: '12px',
  objectFit: 'cover',
};

const fileInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const fileNameStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(255,255,255,0.8)',
  wordBreak: 'break-all',
};

const fileSizeStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'rgba(255,255,255,0.5)',
};

const removeBtnStyle: React.CSSProperties = {
  padding: '6px 16px',
  borderRadius: '8px',
  border: '1px solid rgba(255,100,100,0.4)',
  background: 'rgba(255,100,100,0.1)',
  color: '#ff8a8a',
  fontSize: '13px',
  cursor: 'pointer',
};

const errorStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#ff8a8a',
  textAlign: 'center',
  marginBottom: '20px',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '40px',
};

const btnSecondaryStyle: React.CSSProperties = {
  padding: '12px 24px',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'transparent',
  color: 'rgba(255,255,255,0.7)',
  fontSize: '15px',
  cursor: 'pointer',
};

const btnPrimaryStyle: React.CSSProperties = {
  padding: '12px 32px',
  borderRadius: '10px',
  border: 'none',
  background: 'linear-gradient(135deg, #7c4dff 0%, #448aff 100%)',
  color: '#fff',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
};
